import { useEffect, useSyncExternalStore } from 'react'
import { usePreferences } from '@/store/preferences'

const THEME_COLORS = { dark: '#050b1a', light: '#f3f5fa' } as const

function resolve(pref: 'system' | 'dark' | 'light'): 'dark' | 'light' {
  if (pref !== 'system') return pref
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/** Keeps <html data-theme> and the browser theme-color in sync with preferences. */
export function useThemeSync() {
  const pref = usePreferences((s) => s.theme)

  useEffect(() => {
    const apply = () => {
      const theme = resolve(pref)
      document.documentElement.dataset.theme = theme
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[theme])
    }
    apply()
    if (pref !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [pref])
}

function subscribeTheme(cb: () => void) {
  const obs = new MutationObserver(cb)
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => obs.disconnect()
}

const readTheme = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark')

/** The theme actually applied to the document (follows system changes too). */
export function useResolvedTheme(): 'dark' | 'light' {
  return useSyncExternalStore(subscribeTheme, readTheme, () => 'dark')
}
